import { OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES } from 'notable-common';
import { z } from 'zod';
import { OpenAiMessageRole, VoiceCallTransferCallMode, VoiceCallTransferCallToolDestinationType, } from './vapi.js';
import { FlowBlockValueUsPhoneNumberSchema } from '../../blockValue/phoneNumberSchema.js';
// https://docs.vapi.ai/api-reference/tools/create#request.body.transferCall.destinations.number.transferPlan
export const VoiceCallTransferPlanSchema = z.object({
    mode: z.enum(VoiceCallTransferCallMode),
    // This is the message the assistant will deliver to the destination party before connecting the customer.
    // Used only when mode is:
    // blind-transfer-add-summary-to-sip-header, warm-transfer-say-message,
    // warm-transfer-wait-for-operator-to-speak-first-and-then-say-message, or warm-transfer-experimental.
    message: z.string().optional(),
    // This specifies the SIP verb to use while transferring the call.
    sipVerb: z.partialRecord(z.enum(['refer', 'bye', 'dial']), z.unknown()).optional(),
    // This is the URL of the audio file to play while the call is on hold.
    holdAudioUrl: z.string().optional(),
    // Used when the transfer mode is warm-transfer-twiml.
    // NOTE: This field is automatically set when TransferWithDtmfCallToken mode is selected.
    twiml: z.string().optional(),
    // This is the plan for generating a summary of the call to present to the destination party.
    // Used only when mode is:
    // blind-transfer-add-summary-to-sip-header or warm-transfer-say-summary
    // or warm-transfer-wait-for-operator-to-speak-first-and-then-say-summary or warm-transfer-experimental.
    summaryPlan: z
        .object({
        // These are the messages used to generate the summary.
        // Default: [
        //  {
        //    "role": "system",
        //    "content": "You are an expert note-taker. You will be given a transcript of a call. Summarize the call in 2-3 sentences. DO NOT return anything except the summary."
        //  },
        //  {
        //    "role": "user",
        //    "content": "Here is the transcript:\n\n\{\{transcript}}\n\n"
        //  }
        // ]
        messages: z
            .array(z.object({
            role: z
                .enum([OpenAiMessageRole.System, OpenAiMessageRole.User])
                .default(OpenAiMessageRole.System),
            content: z.string(),
        }))
            .optional(),
        // This determines whether a summary is generated and stored in call.analysis.summary. Defaults to true.
        enabled: z.boolean().optional().default(true),
        // This is how long the request is tried before giving up. When request times out, call.analysis.summary will be empty.
        timeoutSeconds: z.number().optional().default(5),
    })
        .optional(),
    // This flag includes the sipHeaders from above in the refer to sip uri as url encoded query params.
    // Note: purposefully omitting the sipHeadersInReferToEnabled property for now as we are not supporting sip transfer right now
    // sipHeadersInReferToEnabled: z.boolean().optional(),
    // This configures the fallback plan when the transfer fails (destination unreachable, busy, or not human).
    fallbackPlan: z
        .object({
        // This is the message the assistant will deliver to the customer if the transfer fails.
        message: z.string(),
        // This controls what happens after delivering the failure message to the customer.
        // true: End the call after delivering the failure message (default)
        // false: Keep the assistant on the call to continue handling the customer's request
        endCallEnabled: z.boolean().optional().default(true),
    })
        .optional(),
});
// https://docs.vapi.ai/api-reference/tools/create#request.body.transferCall.destinations.number
const FlowVoiceCallTransferCallNumberDestinationSchema = z.object({
    type: z.literal(VoiceCallTransferCallToolDestinationType.Number),
    //This is the phone number to transfer the call to.
    number: FlowBlockValueUsPhoneNumberSchema({
        isRequired: true,
        allowedCountries: OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES,
    }),
    // This is the caller ID to use when transferring the call to the number.
    callerId: z.string().optional(),
    // This is the description of the destination, used by the AI to choose when and how to transfer the call.
    description: z.string().optional(),
    // This is the extension to dial after transferring the call to the number.
    extension: z.string().optional(),
    // This is spoken to the customer before connecting them to the destination.
    message: z.string().optional(),
    // NOTE: purposefully omitting the numberE164CheckEnabled property as it always defaults to true
    // This configures how transfer is executed and the experience of the destination party receiving the call. Defaults to blind-transfer.
    transferPlan: VoiceCallTransferPlanSchema.optional(),
});
const FlowVoiceCallTransferCallSipDestinationSchema = z.object({
    type: z.literal(VoiceCallTransferCallToolDestinationType.Sip),
    // This is the SIP URI to transfer the call to.
    sipUri: z.string(),
    // This is the description of the destination, used by the AI to choose when and how to transfer the call.
    description: z.string().optional(),
    // This is spoken to the customer before connecting them to the destination.
    message: z.string().optional(),
    // These are custom headers to be added to SIP refer during transfer call.
    sipHeaders: z.record(z.string(), z.any()).optional(),
    // This configures how transfer is executed and the experience of the destination party receiving the call. Defaults to blind-transfer.
    transferPlan: VoiceCallTransferPlanSchema.optional(),
});
export const VoiceCallTransferCallToolDestinationSchema = z.discriminatedUnion('type', [
    FlowVoiceCallTransferCallNumberDestinationSchema,
    FlowVoiceCallTransferCallSipDestinationSchema,
]);
//# sourceMappingURL=transferCallTool.js.map